import React, { useContext, useEffect, useState } from "react";
import useBreadcrumbs from "../../../utils/hooks/useBreadcrumbs";
import { MdPerson } from "react-icons/md";
import Button from "../../../UI/Button/Button";
import Alert from "../../../UI/Alert/Alert";
import { NotifyContext } from "../../../context/notifyContext";
import { useNavigate, useParams } from "react-router-dom";
import AdminAPI from "../../../services/api/AdminService";
import { AdminEntity, AdminRoleId } from "../../../Types/admins";
import Input from "../../../UI/Input/Input";
import Select from "../../../UI/Select/Select";

export default function AddOrUpdateAdmin() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const notify = useContext(NotifyContext);
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<{
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    role_id: number;
    avatar?: File | null;
  }>({ email: "", password: "", first_name: "", last_name: "", role_id: AdminRoleId.ADMIN });

  useBreadcrumbs([
    {
      label: "Admins",
      icon: <MdPerson />,
      is_link: true,
      href: "/app/admins",
    },
    {
      label: isEdit ? "Modifier" : "Créer",
      is_link: false,
    },
  ]);

  useEffect(() => {
    if (!isEdit || !id) return;
    AdminAPI.get_admin(Number(id))
      .then((response: any) => response?.data)
      .then((data: any) => {
        const a: AdminEntity = data.admin;
        setForm({
          email: a.email,
          password: "",
          first_name: a.first_name,
          last_name: a.last_name,
          role_id: Number(a.role_id),
        });
        setError("");
      })
      .catch((error) => {
        setError(error);
        notify(error, "error");
      });
  }, [id, isEdit, notify]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    if (isEdit && id) {
      AdminAPI.update_admin(Number(id), {
        first_name: form.first_name,
        last_name: form.last_name,
        avatar: form.avatar,
      })
        .then(() => {
          notify("Mise à jour réussie", "success");
          navigate("/app/admins");
        })
        .catch((err) => notify(err, "error"))
        .finally(() => setLoading(false));
    } else {
      AdminAPI.create_admin({
        email: form.email,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
        role_id: form.role_id,
        avatar: form.avatar,
      })
        .then(() => {
          notify("Création réussie", "success");
          navigate("/app/admins");
        })
        .catch((err) => notify(err, "error"))
        .finally(() => setLoading(false));
    }
  };

  return (
    <div className="container mx-auto my-5 p-5">
      <div className="md:flex no-wrap md:-mx-2 ">
        <div className="w-full md:w-3/12 md:mx-2">
          <div className="bg-white p-3 border-t-4 border-green-400">
            <div className="image overflow-hidden">
              <img
                className="h-auto w-full mx-auto"
                src={
                  "https://lavinephotography.com.au/wp-content/uploads/2017/01/PROFILE-Photography-112.jpg"
                }
                alt=""
              />
            </div>
            <h1 className="text-gray-900 font-bold text-xl leading-8 my-1">
              {form.first_name || "Prénom"} {form.last_name || "Nom"}
            </h1>
            {!isEdit && (
              <ul className="bg-gray-100 text-gray-600 hover:text-gray-700 hover:shadow py-2 px-3 mt-5 divide-y rounded shadow-sm">
                <li className="flex items-center py-3">
                  <span>Email</span>
                  <span className="ml-auto">{form.email || "—"}</span>
                </li>
              </ul>
            )}
          </div>
        </div>

        <div className="w-full md:w-9/12 mx-2 h-64">
          <div className="bg-white p-3 shadow-sm rounded-sm">
            <div className="flex items-center space-x-2 font-semibold text-gray-900 leading-8">
              <span className="text-green-500">
                <svg className="h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </span>
              <span className="tracking-wide">{isEdit ? "Modifier l'admin" : "Créer un admin"}</span>
            </div>
            <div className="text-gray-700">
              <form className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" onSubmit={submit}>
                {!isEdit && (
                  <>
                    <Input
                      label="Email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={(e: any) => setForm((f) => ({ ...f, email: e.target.value }))}
                      required
                    />
                    <Input
                      label="Mot de passe"
                      name="password"
                      type="password"
                      value={form.password}
                      onChange={(e: any) => setForm((f) => ({ ...f, password: e.target.value }))}
                      required
                    />
                    <Select
                      label="Rôle"
                      name="role_id"
                      value={String(form.role_id)}
                      onChange={(e: any) => setForm((f) => ({ ...f, role_id: Number(e.target.value) }))}
                      children={[
                        <option value={String(AdminRoleId.SUPER_ADMIN)}>Super Admin</option>,
                        <option value={String(AdminRoleId.ADMIN)}>Admin</option>,
                      ]}
                    />
                  </>
                )}

                <Input
                  label="Prénom"
                  name="first_name"
                  value={form.first_name}
                  onChange={(e: any) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                  required
                />
                <Input
                  label="Nom"
                  name="last_name"
                  value={form.last_name}
                  onChange={(e: any) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                  required
                />
                <Input
                  label="Avatar"
                  name="avatar"
                  type="file"
                  onChange={(e: any) => setForm((f) => ({ ...f, avatar: e.target.files?.[0] }))}
                />

                <div className="md:col-span-2 flex justify-end">
                  <Button color="primary" type="submit" disabled={loading}>
                    {loading ? "En cours..." : isEdit ? "Mettre à jour" : "Créer"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


